import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Github, ExternalLink, Sparkles, Code2, Layers, Box, Rocket } from 'lucide-react';
import AOS from 'aos';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';

const ProjectsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredProject, setHoveredProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectCategories, setProjectCategories] = useState([
    { id: 'all', label: 'All Projects' },
    { id: 'genai', label: 'Gen AI' },
    { id: 'ai', label: 'AI & Machine Learning' },
    { id: 'fullstack', label: 'Full Stack' },
    { id: 'devops', label: 'DevOps & Cloud' }
  ]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);

  useEffect(() => {
    AOS.init({ once: false, duration: 1000 });
    
    const fetchProjects = async () => {
      try {
        setLoading(true);
        
        const querySnapshot = await getDocs(collection(db, 'projects'));
        const projectsData = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            order: typeof doc.data().order === 'number' ? doc.data().order : 999
          }))
          .filter(project => project.showOnMainSite !== false)
          .sort((a, b) => a.order - b.order);
        
        setProjects(projectsData);
        
        try {
          const tagsDoc = await getDoc(doc(db, 'settings', 'tags'));
          if (tagsDoc.exists()) {
            const categories = tagsDoc.data().filterCategories || projectCategories;
            setProjectCategories(categories);
          }
        } catch (error) {
          console.warn('Could not load categories, using defaults');
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  const handleMouseMove = (e, projectId) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
  };

  const handleMouseLeave = (e) => {
    const card = e.currentTarget;
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  };

  return (
    <section ref={sectionRef} className="relative py-14 sm:py-20 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none dark:opacity-100 opacity-10 transition-opacity duration-300">
        <div className="absolute top-20 left-10 w-56 h-56 sm:w-72 sm:h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-64 h-64 sm:w-96 sm:h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[min(500px,90vw)] h-[min(500px,90vw)] bg-gradient-to-r from-violet-500/5 to-fuchsia-500/5 rounded-full blur-3xl animate-spin-slower" />
      </div>

      {/* Section Header */}
      <div className="relative z-10 mb-12 px-2 text-center sm:mb-16">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="inline-flex flex-wrap items-center justify-center gap-3 mb-6"
        >
          <Sparkles className="w-6 h-6 text-purple-400 animate-pulse sm:w-8 sm:h-8" />
          <h2 
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] via-[#8b5cf6] to-[#a855f7]"
            data-aos="zoom-in"
          >
            Featured Projects
          </h2>
          <Sparkles className="w-6 h-6 text-purple-400 animate-pulse sm:w-8 sm:h-8" />
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto"
          data-aos="fade-up"
        >
          Explore my latest creations, where innovation meets design
        </motion.p>

        {/* 3D Floating Icons */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 sm:gap-8">
          {[Code2, Layers, Box, Rocket].map((Icon, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ 
                scale: 1.2, 
                rotateY: 360,
                transition: { duration: 0.6 }
              }}
              className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 backdrop-blur-sm border border-white/10"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <Icon className="w-6 h-6 text-purple-300" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 flex flex-wrap justify-center gap-3 px-2 mb-10 sm:gap-4 sm:mb-12"
        data-aos="fade-up"
      >
        {projectCategories.map((category) => (
          <motion.button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2.5 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all duration-300 ${
              selectedCategory === category.id
                ? 'bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white shadow-lg shadow-purple-500/50'
                : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
            }`}
          >
            {category.label}
          </motion.button>
        ))}
      </motion.div>

      {/* Loading State */}
      {loading && (
        <div className="relative z-10 flex items-center justify-center py-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full"
          />
        </div>
      )}

      {/* Empty State */}
      {!loading && projects.length === 0 && (
        <div className="relative z-10 text-center py-20">
          <div className="text-6xl mb-4">🎨</div>
          <p className="text-gray-400">No projects to display yet. Add some from the admin panel!</p>
        </div>
      )}

      {/* Projects Grid */}
      {!loading && projects.length > 0 && (
      <div className="relative z-10 grid max-w-7xl grid-cols-1 gap-6 px-2 mx-auto sm:px-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        <AnimatePresence mode="wait">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, scale: 0.8, rotateY: -30 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              exit={{ opacity: 0, scale: 0.8, rotateY: 30 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              onMouseMove={(e) => handleMouseMove(e, project.id)}
              onMouseLeave={handleMouseLeave}
              onMouseEnter={() => setHoveredProject(project.id)}
              className="group relative"
              style={{ 
                transformStyle: 'preserve-3d',
                transition: 'transform 0.1s ease-out'
              }}
              data-aos="flip-up"
              data-aos-delay={index * 100}
            >
              {/* 3D Card Container */}
              <div className="relative h-full overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900/90 via-slate-800/90 to-slate-900/90 backdrop-blur-xl border border-white/10 shadow-2xl">
                {/* Animated Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-blue-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Glowing Border Effect */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-blue-500 to-pink-500 blur-xl opacity-30" />
                </div>

                {/* Featured Badge */}
                {project.featured && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="absolute top-4 left-4 z-20 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold flex items-center gap-1 shadow-lg"
                  >
                    <Sparkles className="w-3 h-3" />
                    Featured
                  </motion.div>
                )}

                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className="w-full h-full"
                  >
                    {project.image && typeof project.image === 'string' && project.image.trim() !== '' ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23334155" width="400" height="300"/%3E%3Ctext fill="%239CA3AF" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="20px"%3EImage Not Found%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full relative flex items-center justify-center bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.03) 10px, rgba(255,255,255,.03) 20px)' }}></div>
                        </div>
                        {/* Content */}
                        <div className="text-center text-gray-400 z-10">
                          <div className="text-6xl mb-2 opacity-50">📷</div>
                          <div className="text-sm font-medium">No Image Available</div>
                          <div className="text-xs mt-1 opacity-70">Add image via admin panel</div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                  
                  {/* Hover Overlay with Links */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: hoveredProject === project.id ? 1 : 0 }}
                    className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent flex items-center justify-center gap-4"
                  >
                    {project.github && project.github.trim() !== '' && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.4 }}
                        className="p-3 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 transition-colors"
                      >
                        <Github className="w-5 h-5 text-white" />
                      </motion.a>
                    )}
                    {project.live && project.live.trim() !== '' && (
                      <motion.a
                        href={project.live}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.4 }}
                        className="p-3 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 text-white" />
                      </motion.a>
                    )}
                  </motion.div>
                </div>

                {/* Project Info */}
                <div className="relative p-4 space-y-4 sm:p-6" style={{ transform: 'translateZ(20px)' }}>
                  <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 group-hover:from-blue-100 group-hover:via-purple-100 group-hover:to-pink-100 transition-all duration-300">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                    {project.description}
                  </p>

                  {/* Tech Tags */}
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, tagIndex) => (
                      <motion.span
                        key={tagIndex}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: tagIndex * 0.1 }}
                        whileHover={{ scale: 1.1, y: -2 }}
                        className="px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-200 border border-purple-500/30 backdrop-blur-sm"
                      >
                        {tag}
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* 3D Depth Layers */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform group-hover:translate-x-2 group-hover:-translate-y-2" style={{ transform: 'translateZ(10px)' }} />
                </div>
              </div>

              {/* Floating Particles on Hover */}
              <AnimatePresence>
                {hoveredProject === project.id && (
                  <>
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                        animate={{
                          opacity: [0, 1, 0],
                          scale: [0, 1, 0],
                          x: Math.random() * 100 - 50,
                          y: Math.random() * 100 - 50,
                        }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 1.5,
                          delay: i * 0.1,
                          repeat: Infinity,
                          repeatDelay: 0.5
                        }}
                        className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 pointer-events-none"
                        style={{ zIndex: -1 }}
                      />
                    ))}
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      )}

      {/* View All Projects Button */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="relative z-10 flex justify-center mt-16"
        data-aos="zoom-in"
      >
        <motion.a
          href="/projects"
          whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(139, 92, 246, 0.5)" }}
          whileTap={{ scale: 0.95 }}
          className="group px-6 py-3 sm:px-8 sm:py-4 rounded-full bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white font-bold text-base sm:text-lg shadow-lg hover:shadow-purple-500/50 transition-all duration-300 flex items-center gap-3"
        >
          <span>View All Projects</span>
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ExternalLink className="w-5 h-5" />
          </motion.div>
        </motion.a>
      </motion.div>
    </section>
  );
};

export default ProjectsSection;
